import { Box, Flex, Text, TextArea } from '@fullstory/components';
import React, { ChangeEvent, useCallback, useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDebounce } from '@fullstory/react-hooks';
import {
  AppearancesContext,
  useDefaultAppearancesPropertyOptions,
  appearancesProperty,
} from '@fullstory/signal-studio-context';

const additionalAppearancesPropertyOptions: appearancesProperty[] = ['text', 'element'];

type SidebarElementPickerProps = {
  selector: string;
  onChange: (selector: string) => void;
};

export const SidebarElementPicker: React.FC<SidebarElementPickerProps> = ({
  selector,
  onChange,
}) => {
  const intl = useIntl();
  const appearances = useContext(AppearancesContext);
  const { Appearances, onPreview } = { ...appearances };

  const handleEditSelector = useCallback(
    (e: ChangeEvent<HTMLTextAreaElement>) => {
      onChange(e.target.value);
    },
    [onChange],
  );

  const _event = useMemo(
    () =>
      selector ? { primary: null, deps: [{ css: { matches: { value: [selector] } } }] } : undefined,
    [selector],
  );
  const event = useDebounce(_event, 800);
  const appearancesPropertyOptions = useDefaultAppearancesPropertyOptions({
    additional: additionalAppearancesPropertyOptions,
  });

  return (
    <Flex
      data-component="SidebarElementPicker"
      column
      justifyContent="flex-start"
      alignItems="stretch"
      gap={4}
      style={{
        flexGrow: 1,
      }}
    >
      <Box paddingTop={8}>
        <Text appearance="bodyMedium">
          <FormattedMessage defaultMessage="Selector" id="gxkld2" />
        </Text>
        <Text appearance="bodyMedium" color="secondary">
          <FormattedMessage defaultMessage="Select from preview or type selector" id="KeDDI/" />
        </Text>
      </Box>
      <TextArea
        value={selector}
        testId="object-picker-selector-input"
        onChange={handleEditSelector}
        onKeyDown={e => {
          if (e.key === 'Enter') {
            e.preventDefault();
          }
        }}
        resizable
        rows={8}
        style={{ wordBreak: 'break-all' }}
      />
      {event && Appearances && (
        <Appearances
          event={event}
          propertyOptions={appearancesPropertyOptions}
          onPreview={
            onPreview
              ? (primary, refinements, _) => onPreview(primary, refinements, _, 0)
              : undefined
          }
          objectType={intl.formatMessage({ defaultMessage: 'selector', id: 'A7ml5w' })}
        />
      )}
    </Flex>
  );
};
